// src/pages/POSCountersPage.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  SimpleGrid,
  Heading,
  Spinner,
  Text,
  Center,
  Badge,
  Stack,
  IconButton,
  useColorModeValue,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Avatar,
  HStack,
  VStack,
  Flex,
  Spacer,
  Tooltip,
  Container,
  InputGroup,
  Input,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  useDisclosure,
  ScaleFade,
  Icon,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  FiChevronRight,
  FiEdit3,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiPlus,
  FiDollarSign,
  FiClock,
  FiMapPin,
} from "react-icons/fi";
import { usePos } from "../../context/PosContext.js";
import NewCounterModal from "./NewCounterModal.jsx";

// Small visually rich card representing the store/counter
const POSCounterCard = ({ counter, onOpen, onEdit, onDelete }) => {
  const {
    isOpen: isHovered,
    onOpen: onHover,
    onClose: onLeave,
  } = useDisclosure();
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const statsBg = useColorModeValue("gray.50", "gray.700");
  const accentColor = useColorModeValue("teal.500", "teal.300");

  const salesToday = counter.metadata?.salesToday ?? null;
  const openTill = counter.metadata?.openTill ?? null;

  return (
    <ScaleFade in={true}>
      <Box
        role="button"
        tabIndex={0}
        borderWidth="1px"
        borderColor={border}
        bg={cardBg}
        p={5}
        borderRadius="2xl"
        boxShadow="sm"
        _hover={{
          transform: "translateY(-6px)",
          boxShadow: "xl",
          borderColor: accentColor,
        }}
        _focus={{ boxShadow: "outline" }}
        transition="all 0.2s ease"
        cursor="pointer"
        onClick={() => onOpen(counter._id)}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onOpen(counter._id);
        }}
        position="relative"
        overflow="hidden"
      >
        {/* Status Indicator */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="4px"
          bg={counter.isActive ? "green.400" : "red.400"}
          transition="all 0.2s"
          opacity={isHovered ? 1 : 0.7}
        />

        <Stack spacing={4}>
          <HStack spacing={4} align="start">
            {/* Store avatar/logo with animation */}
            <Avatar
              size="lg"
              name={counter.name}
              src={counter.logoUrl}
              borderRadius="xl"
              boxShadow="md"
              transition="all 0.2s"
              _hover={{ transform: "scale(1.05)" }}
              bg={accentColor}
            />

            <VStack align="start" spacing={1}>
              <HStack spacing={2} align="center">
                <Heading size="md" noOfLines={1}>
                  {counter.name}
                </Heading>
                <Badge
                  colorScheme={counter.isActive ? "green" : "red"}
                  variant="subtle"
                  px={2}
                  py={1}
                  borderRadius="full"
                  fontSize="xs"
                >
                  {counter.isActive ? "Open" : "Closed"}
                </Badge>
              </HStack>
              <Text fontSize="sm" color="gray.500" fontWeight="medium">
                {counter.store || "Main Store"}
              </Text>
            </VStack>
          </HStack>

          {/* Stats Grid */}
          <SimpleGrid columns={3} spacing={3}>
            {counter.metadata?.location && (
              <HStack
                spacing={2}
                p={2}
                bg={statsBg}
                borderRadius="lg"
                transition="all 0.2s"
                _hover={{ transform: "scale(1.02)" }}
              >
                <Icon as={FiMapPin} color={accentColor} />
                <Text fontSize="sm" color="gray.600">
                  {counter.metadata.location}
                </Text>
              </HStack>
            )}

            {salesToday !== null && (
              <HStack
                spacing={2}
                p={2}
                bg={statsBg}
                borderRadius="lg"
                transition="all 0.2s"
                _hover={{ transform: "scale(1.02)" }}
              >
                <Icon as={FiDollarSign} color={accentColor} />
                <Text fontSize="sm" color="gray.600">
                  ₹{typeof salesToday === "number" ? salesToday : salesToday}
                </Text>
              </HStack>
            )}

            {openTill && (
              <HStack
                spacing={2}
                p={2}
                bg={statsBg}
                borderRadius="lg"
                transition="all 0.2s"
                _hover={{ transform: "scale(1.02)" }}
              >
                <Icon as={FiClock} color={accentColor} />
                <Text fontSize="sm" color="gray.600">
                  {openTill}
                </Text>
              </HStack>
            )}
          </SimpleGrid>

          <Flex pt={2} align="center" justify="space-between">
            <Text fontSize="xs" color="gray.400" fontFamily="mono">
              ID: {counter.uuid?.slice(0, 8)}...
            </Text>

            <HStack spacing={2}>
              <Tooltip label="Edit counter" hasArrow>
                <IconButton
                  aria-label="Edit"
                  icon={<FiEdit3 />}
                  size="sm"
                  colorScheme="teal"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit?.(counter._id);
                  }}
                />
              </Tooltip>

              <Tooltip label="Remove counter" hasArrow>
                <IconButton
                  aria-label="Delete"
                  icon={<FiTrash2 />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(counter._id);
                  }}
                />
              </Tooltip>

              <Button
                rightIcon={<FiChevronRight />}
                size="sm"
                colorScheme="teal"
                variant="solid"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(counter._id);
                }}
              >
                Open
              </Button>
            </HStack>
          </Flex>
        </Stack>
      </Box>
    </ScaleFade>
  );
};

export default function POSCountersPage() {
  const { counters = [], loading, error } = usePos();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");

  const handleOpen = (id) => navigate(`/transactions?posCounterId=${id}`);
  const handleEdit = (id) => navigate(`/poscounters/${id}/edit`);
  const handleDelete = (id) => navigate(`/poscounters/${id}/delete`);

  // Derive filtered lists
  const lists = useMemo(() => {
    const all = counters;
    const active = counters.filter((c) => c.isActive);
    const inactive = counters.filter((c) => !c.isActive);
    return { all, active, inactive };
  }, [counters]);

  // Filter counters based on search and filter
  const filteredCounters = useMemo(() => {
    return lists[selectedFilter].filter(
      (counter) =>
        counter.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        counter.store?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        counter.metadata?.location
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase())
    );
  }, [lists, selectedFilter, searchQuery]);

  if (loading) {
    return (
      <Center py={20}>
        <Spinner size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={20}>
        <Text color="red.500">Error: {error}</Text>
      </Center>
    );
  }

  // helper to render grid or empty state
  const renderGrid = (list) =>
    list.length === 0 ? (
      <Center py={12}>
        <Text>No POS counters found.</Text>
      </Center>
    ) : (
      <SimpleGrid columns={[1, 2, 3]} spacing={4}>
        {list.map((c) => (
          <Box key={c._id}>
            <POSCounterCard
              counter={c}
              onOpen={handleOpen}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </Box>
        ))}
      </SimpleGrid>
    );

  return (
    <Container maxW="container.xl" py={0}>
      <Box
        borderRadius="2xl"
        bg={useColorModeValue("white", "gray.800")}
        boxShadow="sm"
        p={6}
      >
        {/* Header Section */}
        <Stack
          direction={["column", "row"]}
          justify="space-between"
          align="center"
          mb={6}
          spacing={4}
        >
          <VStack align="start" spacing={1}>
            <Heading size="lg">POS Counters</Heading>
            <Text color="gray.500">Manage your point of sale locations</Text>
          </VStack>

          <Button
            leftIcon={<FiPlus />}
            colorScheme="teal"
            size="lg"
            onClick={() => setIsOpen(true)}
            _hover={{ transform: "translateY(-2px)", shadow: "md" }}
          >
            New Counter
          </Button>
        </Stack>

        {/* Search and Filter Bar */}
        <Flex
          gap={4}
          mb={6}
          direction={["column", "row"]}
          align={["stretch", "center"]}
        >
          <InputGroup maxW="400px">
            <InputLeftElement pointerEvents="none">
              <Icon as={FiSearch} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search counters..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              borderRadius="full"
            />
          </InputGroup>

          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<FiFilter />}
              variant="outline"
              borderRadius="full"
            >
              Filter:{" "}
              {selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)}
            </MenuButton>
            <Portal>
              <MenuList>
                <MenuItem onClick={() => setSelectedFilter("all")}>
                  All ({lists.all.length})
                </MenuItem>
                <MenuItem onClick={() => setSelectedFilter("active")}>
                  Active ({lists.active.length})
                </MenuItem>
                <MenuItem onClick={() => setSelectedFilter("inactive")}>
                  Inactive ({lists.inactive.length})
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Flex>

        {/* Counter Grid */}
        {filteredCounters.length === 0 ? (
          <Center py={12} flexDirection="column" gap={4}>
            <Text color="gray.500" fontSize="lg">
              No POS counters found
            </Text>
            <Button
              variant="outline"
              colorScheme="teal"
              onClick={() => {
                setSearchQuery("");
                setSelectedFilter("all");
              }}
            >
              Clear filters
            </Button>
          </Center>
        ) : (
          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing={6}
            transition="all 0.2s"
          >
            {filteredCounters.map((counter) => (
              <POSCounterCard
                key={counter._id}
                counter={counter}
                onOpen={handleOpen}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>
      <NewCounterModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </Container>
  );
}
